import React, { useState, useEffect } from 'react';
import { analyticsAPI } from '../services/api';
import { LineChart, Line, BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { format } from 'date-fns';

const COLORS = ['#667eea', '#764ba2', '#f093fb', '#4facfe', '#43e97b'];

const Performance = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('daily');
  const [days, setDays] = useState(30);

  useEffect(() => {
    loadData();
  }, [period, days]);

  const loadData = async () => {
    setLoading(true);
    try {
      const response = await analyticsAPI.getResponseTimes(period, days);
      setData(response.data.data);
    } catch (error) {
      console.error('Error loading performance data:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <div className="loading">Caricamento...</div>;
  }

  const formatNumber = (value) => {
    return new Intl.NumberFormat('it-IT').format(value || 0);
  };

  const formatTime = (seconds) => {
    if (!seconds) return 'N/A';
    if (seconds < 60) return `${seconds}s`;
    const minutes = Math.floor(seconds / 60);
    const secs = (seconds % 60).toFixed(0);
    return `${minutes}m ${secs}s`;
  };

  // Calculate overall statistics
  const overallStats = {
    totalResponses: data.timeSeries.reduce((sum, item) => sum + (item.response_count || 0), 0),
    avgResponseTime: data.timeSeries.length > 0 
      ? (data.timeSeries.reduce((sum, item) => sum + (parseFloat(item.avg_response_time) || 0), 0) / data.timeSeries.length).toFixed(2)
      : 0,
    minResponseTime: Math.min(...data.timeSeries.map(item => parseFloat(item.min_response_time) || Infinity)),
    maxResponseTime: Math.max(...data.timeSeries.map(item => parseFloat(item.max_response_time) || 0))
  };

  return (
    <div>
      <div className="header">
        <h1>Performance - Tempi di Risposta</h1>
        <div style={{ display: 'flex', gap: '10px' }}>
          <select 
            value={period} 
            onChange={(e) => setPeriod(e.target.value)}
            style={{ padding: '8px', borderRadius: '5px', border: '1px solid #ddd' }}
          >
            <option value="daily">Giornaliero</option>
            <option value="weekly">Settimanale</option>
            <option value="monthly">Mensile</option>
          </select>
          <select 
            value={days} 
            onChange={(e) => setDays(e.target.value)}
            style={{ padding: '8px', borderRadius: '5px', border: '1px solid #ddd' }}
          >
            <option value="7">Ultimi 7 giorni</option>
            <option value="30">Ultimi 30 giorni</option>
            <option value="90">Ultimi 90 giorni</option>
          </select>
        </div>
      </div>

      {/* Statistiche principali */}
      <div className="stats-grid">
        <div className="stat-card">
          <h3>Risposte Totali</h3>
          <div className="stat-value">{formatNumber(overallStats.totalResponses)}</div>
          <div className="stat-change">Nel periodo selezionato</div>
        </div>

        <div className="stat-card">
          <h3>Tempo Medio</h3>
          <div className="stat-value">{formatTime(overallStats.avgResponseTime)}</div>
          <div className="stat-change">Media complessiva</div>
        </div>

        <div className="stat-card">
          <h3>Tempo Minimo</h3>
          <div className="stat-value" style={{ color: '#28a745' }}>
            {formatTime(overallStats.minResponseTime)}
          </div>
          <div className="stat-change">Risposta più veloce</div>
        </div>

        <div className="stat-card">
          <h3>Tempo Massimo</h3>
          <div className="stat-value" style={{ color: '#dc3545' }}>
            {formatTime(overallStats.maxResponseTime)}
          </div>
          <div className="stat-change">Risposta più lenta</div>
        </div>
      </div>

      {/* Grafico andamento temporale */}
      <div className="chart-container">
        <h2>Andamento Tempi di Risposta</h2>
        <ResponsiveContainer width="100%" height={300}>
          <LineChart data={data.timeSeries}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="period" />
            <YAxis label={{ value: 'Secondi', angle: -90, position: 'insideLeft' }} />
            <Tooltip 
              formatter={(value, name) => {
                if (name === 'avg_response_time') return [formatTime(value), 'Tempo Medio'];
                if (name === 'min_response_time') return [formatTime(value), 'Tempo Min'];
                if (name === 'max_response_time') return [formatTime(value), 'Tempo Max'];
                return [value, name];
              }}
            />
            <Legend />
            <Line type="monotone" dataKey="avg_response_time" stroke="#667eea" name="Tempo Medio" strokeWidth={2} />
            <Line type="monotone" dataKey="min_response_time" stroke="#28a745" name="Tempo Min" strokeWidth={1} strokeDasharray="5 5" />
            <Line type="monotone" dataKey="max_response_time" stroke="#dc3545" name="Tempo Max" strokeWidth={1} strokeDasharray="5 5" />
          </LineChart>
        </ResponsiveContainer>
      </div>

      {/* Grafici per prodotto e interfaccia */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginTop: '20px' }}>
        {/* Tempi per prodotto */}
        <div className="chart-container">
          <h2>Tempi di Risposta per Prodotto</h2>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={data.byProduct}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="product_name" />
              <YAxis label={{ value: 'Secondi', angle: -90, position: 'insideLeft' }} />
              <Tooltip 
                formatter={(value, name) => {
                  if (name === 'avg_response_time') return [formatTime(value), 'Tempo Medio'];
                  if (name === 'response_count') return [formatNumber(value), 'Risposte'];
                  return [value, name];
                }}
              />
              <Legend />
              <Bar dataKey="avg_response_time" fill="#667eea" name="Tempo Medio" />
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Tempi per interfaccia */}
        <div className="chart-container">
          <h2>Tempi di Risposta per Interfaccia</h2>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={data.byInterface}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="interface_type" />
              <YAxis label={{ value: 'Secondi', angle: -90, position: 'insideLeft' }} />
              <Tooltip 
                formatter={(value, name) => {
                  if (name === 'avg_response_time') return [formatTime(value), 'Tempo Medio'];
                  if (name === 'response_count') return [formatNumber(value), 'Risposte'];
                  return [value, name];
                }}
              />
              <Legend />
              <Bar dataKey="avg_response_time" fill="#764ba2" name="Tempo Medio" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Distribuzione tempi di risposta */}
      <div className="chart-container" style={{ marginTop: '20px' }}>
        <h2>Distribuzione Tempi di Risposta</h2>
        <ResponsiveContainer width="100%" height={300}>
          <BarChart data={data.distribution}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="time_bucket" />
            <YAxis label={{ value: 'Numero di Risposte', angle: -90, position: 'insideLeft' }} />
            <Tooltip 
              formatter={(value) => [formatNumber(value), 'Risposte']}
            />
            <Legend />
            <Bar dataKey="count" fill="#f093fb" name="Numero di Risposte">
              {data.distribution.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* Tabella dettagliata per prodotto */}
      <div className="table-container" style={{ marginTop: '20px' }}>
        <h2>Dettaglio per Prodotto</h2>
        <table>
          <thead>
            <tr>
              <th>Prodotto</th>
              <th>Risposte</th>
              <th>Tempo Medio</th>
              <th>Tempo Min</th>
              <th>Tempo Max</th>
            </tr>
          </thead>
          <tbody>
            {data.byProduct.map((item, index) => (
              <tr key={index}>
                <td>{item.product_name}</td>
                <td>{formatNumber(item.response_count)}</td>
                <td>{formatTime(item.avg_response_time)}</td>
                <td style={{ color: '#28a745' }}>{formatTime(item.min_response_time)}</td>
                <td style={{ color: '#dc3545' }}>{formatTime(item.max_response_time)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Tabella dettagliata per interfaccia */}
      <div className="table-container" style={{ marginTop: '20px' }}>
        <h2>Dettaglio per Interfaccia</h2>
        <table>
          <thead>
            <tr>
              <th>Interfaccia</th>
              <th>Risposte</th>
              <th>Tempo Medio</th>
              <th>Tempo Min</th>
              <th>Tempo Max</th>
            </tr>
          </thead>
          <tbody>
            {data.byInterface.map((item, index) => (
              <tr key={index}>
                <td>{item.interface_type}</td>
                <td>{formatNumber(item.response_count)}</td>
                <td>{formatTime(item.avg_response_time)}</td>
                <td style={{ color: '#28a745' }}>{formatTime(item.min_response_time)}</td>
                <td style={{ color: '#dc3545' }}>{formatTime(item.max_response_time)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default Performance;

